import type { ColorParam, ColorType } from './types';

/**
 * Converts Hex string (e.g. "#F5F0E8") to RGB object
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let cleanHex = hex.replace('#', '').trim();
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = parseInt(cleanHex, 16);
  if (isNaN(num)) {
    return { r: 240, g: 240, b: 240 };
  }
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

/**
 * Calculates perceived brightness (luminance) from RGB
 * Formula: Y = 0.299*R + 0.587*G + 0.114*B
 */
export function calculateLuminance(r: number, g: number, b: number): number {
  return Math.round(0.299 * r + 0.587 * g + 0.114 * b);
}

/**
 * Determines if a color is Light (>128) or Dark (<=128)
 */
export function getColorType(r: number, g: number, b: number): ColorType {
  const luminance = calculateLuminance(r, g, b);
  return luminance > 128 ? 'Light' : 'Dark';
}

/**
 * Generates complete ColorParam object from Name, Hex, and optional Shade Code
 */
export function createColorParam(name: string, hex: string, code?: string): ColorParam {
  const { r, g, b } = hexToRgb(hex);
  const type = getColorType(r, g, b);
  return {
    name,
    code: code || '',
    hex: hex.toUpperCase(),
    rgb: `${r}, ${g}, ${b}`,
    type,
  };
}
